import AdminLoginWrapper from 'client/containers/Login/AdminLoginWrapper.vue';
import AdminTotpSetup from 'client/containers/AdminTotpSetup/AdminTotpSetup.vue';
import ChooseMfa from 'client/containers/Login/ChooseMfa.vue';
import Constants from 'client/util/Constants';
import LoginApp from 'client/containers/LoginApp.vue';
import LoginConsts from 'client/util/Constants/LoginConstants';
import MfaConstants from 'client/containers/Login/MfaConstants';
import MfaType from 'client/containers/Login/MfaType';
import ProviderNotLinked from 'client/containers/ProviderNotLinked/ProviderNotLinked.vue';
import RouteGenerator, { VueRouterMode } from 'client/routes/RouteGenerator';
import UserApi from 'client/api/v2/UserApi';
import UserLoginApi from 'client/api/UserLoginApi';
import Util from 'client/util/Util';
import VerifyDuo from 'client/containers/VerifyDuo.vue';
import VerifyMobilePush from 'client/containers/VerifyMobilePush.vue';
import VerifyTOTP from 'client/containers/VerifyTOTP.vue';
import VerifyWebAuthN from 'client/containers/VerifyWebAuthN';
import VueRouter from 'vue-router';
import loginStore from 'client/stores/loginStore';

const USER = LoginConsts.clientType.user;
const { redirectTo } = Constants;
const { userConsoleRootRoute } = LoginConsts;
const { adminPortalLogin, adminPortalTotpSetup, userPortalLogin } = LoginConsts.documentTitles;
const { mfaStatus } = MfaConstants;
const { login, loginAdmin } = RouteGenerator;

/**
 * This middleware was created to validate if the user has an active session,
 * if it does... the user does not need to go the login page,
 * so we avoid the router to render the login, and then we redirect the user to the /consoleUser#
*/
export const SessionValidatorPipeline = (routeInfo) => {
  const { next, parameters = {} } = routeInfo;
  const { currentRoute = {} } = parameters;

  // added an addition check for a template as the LoginApp routing was causing an issue
  if (currentRoute.query && (currentRoute.query.context || currentRoute.query.template)) {
    next();
    return;
  }

  currentRoute.meta.getUser()
    .then(() => Util.redirect(currentRoute.meta.loggedInRoute))
    .catch(() => next());
};

export const preprocessRoute = (currentRoute, from, callback) => {
  const { pipeline } = currentRoute.meta || { };

  if (pipeline && pipeline.length) {
    Util.chainPipelineSegments(pipeline.concat([callback]), { currentRoute, from })();
  } else callback();
};

const processTitle = (currentRoute, from, callback) => {
  const { title } = currentRoute.meta || { };

  document.title = title || currentRoute.name || 'JumpCloud';

  callback();
};

const parseFactorsParam = (factorNames = '') => factorNames
  .split(',')
  .filter(factorString => factorString.length)
  .reduce((accumulator, factorName) => {
    accumulator.push(MfaType({
      factor: {
        type: factorName,
        status: mfaStatus.available,
        clientType: USER,
      },
    }));

    return accumulator;
  }, []);

// check that fullPath includes verifyemail then store hash in sessionStorage
export const verifyEmailCheck = (routeInfo) => {
  const { next, parameters = {} } = routeInfo;
  const { currentRoute = {} } = parameters;
  if (currentRoute.fullPath.includes('verifyemail')) {
    sessionStorage.setItem(redirectTo, `${userConsoleRootRoute}${currentRoute.hash}`);
  }
  next();
};

/**
 * Need this to guard against users navigating to /:factor without params
 * Example: Login, land on /choosemfa, click back button, click forward button
 * User appears logged in, but we've lost the necessary params to complete auth
 */
const verifyUser = (to, _, next) => {
  let path;

  if (!to.params.clientType) {
    path = login;
  }

  next(path);
};

export const RouterOptions = {
  mode: VueRouterMode,
  routes: [
    {
      path: login,
      name: 'Login',
      component: LoginApp,
      props: true,
      meta: {
        loggedInRoute: userConsoleRootRoute,
        getUser: () => UserLoginApi.getUser(),
        pipeline: [SessionValidatorPipeline, verifyEmailCheck],
        title: userPortalLogin,
      },
      /**
       * This "beforeEach" callback needs to be added in order to support custom router middlewares
       * or pipelines as the SessionValidatorPipeline.
       */
      beforeEnter: preprocessRoute,
    },
    {
      path: loginAdmin,
      name: 'Admin Login',
      component: AdminLoginWrapper,
      props: route => ({
        ...route.params,
        error: route.params.error || route.query.error,
      }),
      meta: {
        loggedInRoute: '/#/home',
        getUser: () => UserApi.getSession(),
        pipeline: [SessionValidatorPipeline],
        title: adminPortalLogin,
      },
      beforeEnter: preprocessRoute,
    },
    {
      path: `/${RouteGenerator.adminTotpSetup}`,
      name: RouteGenerator.adminTotpSetup,
      component: AdminTotpSetup,
      props: true,
      meta: {
        title: adminPortalTotpSetup,
      },
      beforeEnter: preprocessRoute,
    },
    {
      path: `/${RouteGenerator.chooseMfa}`,
      name: RouteGenerator.chooseMfa,
      component: ChooseMfa,
      props: true,
    },
    {
      path: `/${RouteGenerator.duo2fa}`,
      name: RouteGenerator.duo2fa,
      component: VerifyDuo,
      props: true,
      beforeEnter: verifyUser,
    },
    {
      path: `/${RouteGenerator.totp}`,
      name: RouteGenerator.totp,
      component: VerifyTOTP,
      props: true,
      beforeEnter: verifyUser,
    },
    {
      path: `/${RouteGenerator.webauthn}`,
      name: RouteGenerator.webauthn,
      component: VerifyWebAuthN,
      props: true,
      beforeEnter: verifyUser,
    },
    {
      path: `/${RouteGenerator.push}`,
      name: RouteGenerator.push,
      component: VerifyMobilePush,
      props: true,
      beforeEnter: verifyUser,
    },
    {
      path: RouteGenerator.loginProviderNotLinked,
      name: 'Provider Not Linked',
      component: ProviderNotLinked,
    },
    {
      path: '/login/stepup/',
      name: 'Stepup Choose MFA',
      alias: `/${RouteGenerator.chooseMfa}`,
      component: ChooseMfa,
      props: (route) => {
        const factors = parseFactorsParam(route.query.factors || '');
        const state = route.query && route.query.state;

        loginStore.dispatch('StepUpModel/setStateId', state);

        return {
          ...route.query,
          ...route.params,
          factors,
          clientType: USER,
        };
      },
      beforeEnter: (to, _, next) => {
        let path;

        if (!to.query || !to.query.state) {
          path = login;
        }

        next(path);
      },
    },
    {
      path: '*',
      redirect: {
        name: 'Login',
      },
    },
  ],
};

const router = new VueRouter(RouterOptions);

router.beforeEach(processTitle);

export default router;
